import {
  AlignmentMeasurementTool,
  AngleMeasurement,
  AngleMeasurementTool,
  Markup,
  MarkupEventName,
  MarkupPlugin,
  Measurement as Web3DLineMeasurement,
  MeasurementTool,
  ImagePosition,
  PointMarkupTool,
  PolylineMeasurementTool,
  PolylineMeasurement,
  PolygonMeasurementTool,
  PolygonMeasurement,
} from '@technology/web3d-plugin-markup';
import { Color, Web3DViewer, SnapPick, Vector3 } from '@technology/web3d';
import {
  detectArrayChanges,
  calculateDistance,
  partialPointToVector3,
  chainDistance,
  geoValuesEqual,
  RGBFormat,
} from '@trimble/utilities';
import { Web3DViewerWrapper, rgbToWeb3dColor } from '@trimble/web3d-utils';
import {
  Accessor,
  batch,
  createEffect,
  createMemo,
  createSignal,
  on,
  Setter,
} from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { SnapType } from '../snapping/snap-type';
import {
  AngleMeasurementType,
  Measurement,
  MeasurementType,
  PointMeasurementType,
  PointMeasurementSettings,
  LineMeasurementType,
  PolylineMeasurementType,
  SlopeFormat,
  MeasurementSaveStatus,
  AreaMeasurementType,
  DEFAULT_POLYGON_MEASUREMENT_SETTINGS,
  LineStyle,
  VolumeMeasurementType,
} from './measurement';
import { SharedCapabilityProviderBase } from '../base/shared-provider-base';
import { MeasurementStyleManager } from './measurement-style-manager';
import {
  convertMarkupToMeasurement,
  createAngleMeasurement,
  transformTcMarkupToMeasurement,
  MeasurementMarkup,
  processMarkup,
  applyPointLabel,
  getLabelFromMarkup,
  slopeFormatter,
  clonePicks,
  boundaryInclusiveToSnapPicks,
  VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX,
  getBoundaryPointsFromPolygonMarkup,
} from './measurement-helpers';
import { setTilesetModelOpacity } from './volume-tileset-opacity';
import { Markup as ConnectMarkup } from '@data-access/connect/tc-tcps';
import { AngleUnit, AreaUnit, LengthUnit } from '@fulcrum/lib';
import { AlignmentSettings, FormattingSettings } from '@trimble/types';
import { getLogger } from '@trimble/logging';
import {
  applyChainageFormatterToAlignmentMeasurements,
  applyChainageFormatterToMarkup,
  applyFormattingSettingsToViewers,
  MeasurementFormattingContext,
} from './measurement-viewer-formatting';
import {
  MeasurementSeedContext,
  pruneMeasurementsViewerRefs,
  projectMeasurementsOntoViewer,
} from './measurement-viewer-lifecycle';
import { stampCanonicalId } from '../base/viewer-annotation-identity';

/** The base unit for length measurements. */
const BASE_LENGTH_UNIT = 'm';
const BASE_ANGLE_UNIT = 'rad';
const BASE_AREA_UNIT = 'm2';

/* eslint-disable no-magic-numbers -- Default magenta color RGB values (236, 0, 182) */
const RGB_FACTOR = 255;
const DEFAULT_MAGENTA_COLOR = new Color(
  236 / RGB_FACTOR,
  0 / RGB_FACTOR,
  182 / RGB_FACTOR
);
/* eslint-enable no-magic-numbers */

const SELECTED_MARKUP_RENDER_ORDER = 1;
const DEFAULT_MARKUP_RENDER_ORDER = 0;

/**
 * Sentinel origin used to tag measurement markups created via cross-pane replication.
 * When `markupmodified` fires with this origin, we skip it to avoid phantom duplicates.
 * Capabilities that listen to `markupadded` must also ignore these markups — the
 * measurements provider already applied the stored label during projection.
 */
export const MEASUREMENT_REPLICATION_ORIGIN = Symbol(
  'MeasurementsProvider.replication'
);

const REPLICATION_ORIGIN = MEASUREMENT_REPLICATION_ORIGIN;

export interface ToggleMeasurementVisibilityOptions {
  readonly keepSelection?: boolean;
}

export class MeasurementsCapabilityProvider extends SharedCapabilityProviderBase<Measurement> {
  public idKey = 'id' as const;
  private readonly viewerEventListeners = new Map<
    Web3DViewer,
    Array<{ event: string; handler: EventListener }>
  >();
  private readonly styleManager = new MeasurementStyleManager();

  /**
   * When true, markup click/selection events are ignored and will not change the
   * selected measurement. Set this while a panel (e.g. volumetric create) needs
   * to intercept markup picks without triggering measurement navigation.
   */
  private isMarkupSelectionSuppressedFlag = false;
  private markupSelectionSuppressionRefCount = 0;

  public isMarkupSelectionSuppressed(): boolean {
    return (
      this.isMarkupSelectionSuppressedFlag ||
      this.markupSelectionSuppressionRefCount > 0
    );
  }

  public setMarkupSelectionSuppressed(value: boolean): void {
    this.isMarkupSelectionSuppressedFlag = value;
  }

  /**
   * Temporarily suppress markup-driven selection changes (e.g. across a volume
   * tileset URL change). Returns a release function that is safe to call more than
   * once. Coexists with `setMarkupSelectionSuppressed` — suppression is active
   * while either the boolean flag is set or the refcount is above zero.
   */
  public acquireMarkupSelectionSuppression(): () => void {
    this.markupSelectionSuppressionRefCount += 1;
    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      this.markupSelectionSuppressionRefCount = Math.max(
        0,
        this.markupSelectionSuppressionRefCount - 1
      );
    };
  }

  /**
   * When set, the volume measurement tileset with this ID will not be rendered.
   * Used after a recompute preview is available so the stale saved heatmap
   * does not overlay the new result. Edit/draw of a saved volume should not
   * set this until that preview exists.
   */
  public readonly tilesetDisplaySuppressedMeasurementId: Accessor<
    string | undefined
  >;
  private readonly setTilesetDisplaySuppressedMeasurementIdInternal: Setter<
    string | undefined
  >;

  public setTilesetDisplaySuppressedMeasurementId(
    id: string | undefined
  ): void {
    this.setTilesetDisplaySuppressedMeasurementIdInternal(id);
  }

  public readonly lengthUnit: LengthUnit = BASE_LENGTH_UNIT;
  public readonly angleUnit: AngleUnit = BASE_ANGLE_UNIT;
  public readonly areaUnit: AreaUnit = BASE_AREA_UNIT;
  public readonly formattingSettings: Accessor<FormattingSettings>;
  public readonly enableMeasurementSelection: Accessor<boolean>;
  public readonly selectedMeasurement: Accessor<Measurement | undefined>;
  public readonly pointMeasurementSettings: Accessor<PointMeasurementSettings>;
  public readonly setPointMeasurementSettings: Setter<PointMeasurementSettings>;
  public readonly slopeFormat: Accessor<SlopeFormat>;
  public readonly setSlopeFormat: Setter<SlopeFormat>;
  private readonly alignmentSettings: Accessor<AlignmentSettings | undefined>;

  public cleanup(): Promise<void> {
    this.clearMarkupSelection();
    this.removeAllMeasurements();
    this.removeAllEventListeners();
    return Promise.resolve(undefined);
  }

  public override removeViewer(viewer: Web3DViewerWrapper): void {
    batch(() => {
      pruneMeasurementsViewerRefs(viewer, this.setItems, (markups) =>
        this.getValidMarkups(markups)
      );
      this.removeEventListenersForViewer(viewer);
      super.removeViewer(viewer);
    });
  }

  constructor(
    formattingSettings: () => FormattingSettings,
    enableMeasurementSelection?: () => boolean,
    alignmentSettings?: () => AlignmentSettings | undefined
  ) {
    super();

    const [
      tilesetDisplaySuppressedMeasurementId,
      setTilesetDisplaySuppressedMeasurementIdInternal,
    ] = createSignal<string | undefined>();
    this.tilesetDisplaySuppressedMeasurementId =
      tilesetDisplaySuppressedMeasurementId;
    this.setTilesetDisplaySuppressedMeasurementIdInternal =
      setTilesetDisplaySuppressedMeasurementIdInternal;

    this.formattingSettings = formattingSettings;
    this.alignmentSettings = alignmentSettings ?? (() => undefined);
    this.enableMeasurementSelection =
      enableMeasurementSelection ?? (() => true);

    const [pointMeasurementSettings, setPointMeasurementSettings] =
      createSignal<PointMeasurementSettings>({
        showXYCoordinates: true,
        showZCoordinate: true,
      });

    this.pointMeasurementSettings = pointMeasurementSettings;
    this.setPointMeasurementSettings = setPointMeasurementSettings;

    const [slopeFormat, setSlopeFormat] = createSignal<SlopeFormat>('ratio');
    this.slopeFormat = slopeFormat;
    this.setSlopeFormat = setSlopeFormat;

    // Create memo for selected measurement
    this.selectedMeasurement = createMemo(
      () => this.selectedItem() ?? undefined
    );

    // Sync formatting settings.
    createEffect(
      on(
        [this.viewerWrappers, formattingSettings],
        ([viewerWrappers, formattingSettings], prev) => {
          const [prevViewerWrappers, prevFormattingSettings] = prev ?? [
            undefined,
            undefined,
          ];

          const { newItems } = detectArrayChanges(
            prevViewerWrappers,
            viewerWrappers
          );

          if (newItems.length > 0) {
            // Apply settings to new viewers only
            applyFormattingSettingsToViewers(
              this.getFormattingContext(),
              formattingSettings,
              newItems
            );
          } else if (prevFormattingSettings !== formattingSettings) {
            // Apply settings to all viewers when length unit or decimal places change
            applyFormattingSettingsToViewers(
              this.getFormattingContext(),
              formattingSettings,
              viewerWrappers
            );
          }

          // Re-apply chainage formatting to alignment measurements after the
          // viewer's customLengthFormatter has been (re-)set, because the web3d
          // library synchronously re-formats all labels with the generic formatter
          // when customLengthFormatter changes.
          applyChainageFormatterToAlignmentMeasurements(
            this.getFormattingContext()
          );
        }
      )
    );

    // Re-apply chainage formatting when alignment settings change.
    if (alignmentSettings) {
      createEffect(
        on(alignmentSettings, () => {
          applyChainageFormatterToAlignmentMeasurements(
            this.getFormattingContext()
          );
        })
      );
    }

    // Sync markup selection.
    createEffect(
      on(
        [this.selectedMeasurement, this.enableMeasurementSelection],
        ([measurement, enableMeasurementSelection]) => {
          // This is configured to disable measurement selection by blocking
          // this select action. This was needed to support Tiering implementation
          // @see https://trimble-arch.aha.io/features/WM-648
          // ToDo: Improve this disable logic to use a standardised pattern for
          //  Tiering, project roles, feature flagging and reduced linearity
          this.resetAllMarkupsRenderOrder();

          if (!measurement || !enableMeasurementSelection) {
            this.clearMarkupSelection();
            this.clearVolumeTilesetSelection();
            return;
          }

          for (const viewerWrapper of this.viewerWrappers()) {
            const markups = this.getViewerMarkups(
              viewerWrapper.viewer,
              measurement
            );
            if (markups.length === 0) continue;

            // Sometimes Web3D dependency would crash here and program flow would be
            // broken - side panel for measurements properties wouldn't open. So handling
            // it with try/catch.
            try {
              viewerWrapper.viewer.selection.set(
                'markup',
                markups.map((m) => m.id)
              );
            } catch (error) {
              getLogger().warn('Failed to set markup selection', error);
            }
            this.bringMarkupsToFront(markups);
          }
        }
      )
    );

    // Sync markup deletion.
    createEffect(
      on(this.items, (current, prev) => {
        if (prev == undefined) {
          return;
        }
        const deletedMeasurements = prev.filter(
          (prevItem) =>
            // Check if measurements has some markups in common (so it's same markup).
            !current.some((currItem) =>
              this.getValidMarkups(currItem.markups).some((ms) =>
                this.getValidMarkups(prevItem.markups).some(
                  (m) => m.id == ms.id
                )
              )
            )
        );

        deletedMeasurements.forEach((item) => {
          const ids = this.getValidMarkups(item.markups).map((m) => m.id);
          for (const viewerWrapper of this.viewerWrappers()) {
            const markupPlugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
            if (!markupPlugin) {
              throw new Error('MarkupPlugin is not available on the viewer');
            }

            const markupIds = markupPlugin
              .getMarkups()
              .filter((m) => ids.some((id) => id === m.id))
              .map((m) => m.id);

            if (markupIds.length) {
              markupPlugin.removeById(markupIds);
            }
          }
        });

        return [...current];
      })
    );

    // Sync from viewer to side panel.
    createEffect(
      on(this.viewerWrappers, (viewerWrappers, previousViewerWrappers) => {
        const { newItems: newViewerWrappers, removedItems: removedViewers } =
          detectArrayChanges(previousViewerWrappers, viewerWrappers);

        // Remove event listeners from removed viewers
        removedViewers.forEach((viewer) => {
          this.removeEventListenersForViewer(viewer);
        });

        batch(() => {
          for (const viewerWrapper of newViewerWrappers) {
            this.addEventListenersToViewer(
              viewerWrapper,
              previousViewerWrappers
            );
            projectMeasurementsOntoViewer(
              this.getSeedContext(),
              viewerWrapper,
              REPLICATION_ORIGIN
            );
          }
        });
      })
    );
  }

  private getFormattingContext(): MeasurementFormattingContext {
    return {
      items: () => this.items(),
      formattingSettings: () => this.formattingSettings(),
      alignmentSettings: () => this.alignmentSettings(),
      getViewerMarkups: (viewer, measurement) =>
        this.getViewerMarkups(viewer, measurement),
      getValidMarkups: (markups) => this.getValidMarkups(markups),
    };
  }

  private getSeedContext(): MeasurementSeedContext {
    return {
      items: () => this.items(),
      setItems: this.setItems,
      applyFormatting: (viewer) =>
        applyFormattingSettingsToViewers(
          this.getFormattingContext(),
          this.formattingSettings(),
          [viewer]
        ),
      createOnViewer: (measurement, viewer, origin) => {
        const colorToUse = measurement.color
          ? rgbToWeb3dColor(measurement.color)
          : undefined;
        this.createMeasurement(measurement, [viewer], colorToUse, origin);
      },
      setHiddenOnViewer: (measurement, _viewer) => {
        const markup = this.getValidMarkups(measurement.markups).at(-1);
        if (markup) {
          this.setMarkupVisibility(markup, false);
        }
      },
    };
  }

  // During initialization markups can be undefined, so we are filtering that case
  private getValidMarkups(markups: Markup[]): Markup[] {
    return markups.filter((m): m is Markup => m != null);
  }

  private findMeasurementForMarkup(markup: Markup): Measurement | undefined {
    return this.items().find((measurement) =>
      this.getValidMarkups(measurement.markups).some((m) => markup.id === m.id)
    );
  }

  /**
   * Checks if a measurement exists for the given markup and updates it if changed.
   *
   * @param markup - A new or modified markup object.
   * @returns true if an existing measurement was found and updated, false otherwise.
   */
  private updateMeasurementForMarkup(markup: Markup): boolean {
    const measurement = this.findMeasurementForMarkup(markup);
    if (measurement) {
      switch (measurement.type) {
        case 'point':
          this.syncPointMeasurement(measurement, markup);
          break;
        case 'line':
          this.syncLineMeasurement(measurement, markup);
          break;
        case 'polyline':
          this.syncPolylineMeasurement(
            measurement,
            markup as PolylineMeasurement
          );
          break;
        case 'area':
          this.syncAreaMeasurement(measurement, markup as PolygonMeasurement);
          break;
        case 'angle':
          this.syncAngleMeasurement(measurement, markup);
          break;
        case 'alignment':
        case 'volume':
        case 'passCount':
          break;
        default:
          throw new UnreachableCaseError(measurement);
      }
      return true;
    }
    return false;
  }

  private syncPointMeasurement(
    measurement: PointMeasurementType,
    markup: Markup
  ): void {
    if (
      measurement.x === markup.position.x &&
      measurement.y === markup.position.y &&
      measurement.z === markup.position.z
    ) {
      return;
    }

    const newMarkups = this.getValidMarkups(measurement.markups).map(
      (existingMarkup) => {
        if (existingMarkup.id !== markup.id) {
          existingMarkup.position.copy(markup.position);
        }
        return existingMarkup;
      }
    );
    const updatedMeasurement: PointMeasurementType = {
      ...measurement,
      x: markup.position.x,
      y: markup.position.y,
      z: markup.position.z,
      markups: newMarkups,
    };
    this.updateMeasurement(updatedMeasurement);
  }

  /**
   * Shared sync flow: detect change, find source viewer, build updated measurement,
   * update state, and re-create markup in other viewers. Parameterized by change
   * detection and a measurement-builder callback.
   *
   * NOTE — push-after-setItems pattern: this method calls `updateMeasurement`
   * (which calls `setItems`) with only the source viewer's markup, then
   * `createMeasurement` pushes replica markups onto `updatedMeasurement.markups`
   * afterward. Because the stored item shares the same array reference the
   * push is visible via `this.items()`, but it bypasses SolidJS reactivity —
   * no `createEffect` depending on `items()` will re-run for the markup count
   * change. The safer pattern (used in `markups-provider.ts`) is to collect
   * all replica markups first and call `setItems` once with the fully-populated
   * list. Refactoring this would also eliminate the microtask race window that
   * NUMARK-797 exposed.
   *
   * TODO: refactor to collect all replicas before calling `updateMeasurement`,
   * matching the approach in `MarkupsCapabilityProvider.syncMarkupWithViewerMarkup`.
   */
  private syncMeasurementWithMarkup<M extends Measurement>(
    measurement: M,
    markup: Markup,
    hasChanged: boolean,
    buildUpdated: (measurement: M, sourceViewer: Web3DViewer) => M,
    errorContext: string
  ): void {
    if (!hasChanged) {
      return;
    }

    const sourceWrapper = this.viewerWrappers().find((vw) =>
      this.getViewerMarkups(vw.viewer, measurement).some(
        (m) => m.id === markup.id
      )
    );
    if (!sourceWrapper) {
      getLogger().error(errorContext, {
        measurementId: measurement.id,
        markupId: markup.id,
      });
      return;
    }

    const sourceViewer = sourceWrapper.viewer;
    const updatedMeasurement = buildUpdated(measurement, sourceViewer);
    this.updateMeasurement(updatedMeasurement);

    for (const viewerWrapper of this.viewerWrappers()) {
      if (viewerWrapper.viewer === sourceViewer) {
        continue;
      }
      const existingMarkups = this.getViewerMarkups(
        viewerWrapper.viewer,
        measurement
      );
      viewerWrapper.markupPlugin.removeById(existingMarkups.map((m) => m.id));
      this.createMeasurement(
        updatedMeasurement,
        [viewerWrapper],
        undefined,
        REPLICATION_ORIGIN
      );
    }
  }

  private syncLineMeasurement(
    measurement: LineMeasurementType,
    markup: Markup
  ): void {
    if (!(markup instanceof Web3DLineMeasurement)) {
      getLogger().warn('Line measurement markup is not a LineMarkup instance', {
        measurementId: measurement.id,
        markup,
      });
      return;
    }

    const mainLine = markup.getMainLine();
    const startPoint = mainLine.start.clone();
    const endPoint = mainLine.end.clone();
    const startChanged =
      !geoValuesEqual(measurement.startPoint.x, startPoint.x) ||
      !geoValuesEqual(measurement.startPoint.y, startPoint.y) ||
      !geoValuesEqual(measurement.startPoint.z, startPoint.z);
    const endChanged =
      !geoValuesEqual(measurement.endPoint.x, endPoint.x) ||
      !geoValuesEqual(measurement.endPoint.y, endPoint.y) ||
      !geoValuesEqual(measurement.endPoint.z, endPoint.z);

    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      startChanged || endChanged,
      (m, sourceViewer) => ({
        ...m,
        startPoint,
        endPoint,
        value: calculateDistance(startPoint, endPoint),
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Line measurement markup not found in viewers'
    );
  }

  private syncPolylineMeasurement(
    measurement: PolylineMeasurementType,
    markup: PolylineMeasurement
  ): void {
    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      this.hasPicksChanged(measurement.picks, markup.picks),
      (m, sourceViewer) => ({
        ...m,
        picks: clonePicks(markup.picks),
        value: chainDistance(markup.picks),
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Polyline measurement markup not found in viewers'
    );
  }

  private syncAreaMeasurement(
    measurement: AreaMeasurementType,
    markup: PolygonMeasurement
  ): void {
    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      this.hasPicksChanged(measurement.picks, markup.picks),
      (m, sourceViewer) => ({
        ...m,
        picks: clonePicks(markup.picks),
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Area measurement markup not found in viewers'
    );
  }

  private syncAngleMeasurement(
    measurement: AngleMeasurementType,
    markup: Markup
  ): void {
    if (!(markup instanceof AngleMeasurement)) {
      getLogger().warn(
        'Angle measurement markup is not an AngleMeasurement instance',
        { measurementId: measurement.id, markup }
      );
      return;
    }

    let updatedMeasurement: AngleMeasurementType;
    try {
      updatedMeasurement = createAngleMeasurement(
        markup,
        measurement.label || ''
      ) as AngleMeasurementType;
    } catch (e) {
      getLogger().warn('Failed to create angle measurement for sync', {
        measurementId: measurement.id,
        markup,
        error: e,
      });
      return;
    }

    const startChanged =
      !geoValuesEqual(
        measurement.startPoint.x,
        updatedMeasurement.startPoint.x
      ) ||
      !geoValuesEqual(
        measurement.startPoint.y,
        updatedMeasurement.startPoint.y
      ) ||
      !geoValuesEqual(
        measurement.startPoint.z,
        updatedMeasurement.startPoint.z
      );
    const endChanged =
      !geoValuesEqual(measurement.endPoint.x, updatedMeasurement.endPoint.x) ||
      !geoValuesEqual(measurement.endPoint.y, updatedMeasurement.endPoint.y) ||
      !geoValuesEqual(measurement.endPoint.z, updatedMeasurement.endPoint.z);
    const middleChanged =
      !geoValuesEqual(
        measurement.middlePoint.x,
        updatedMeasurement.middlePoint.x
      ) ||
      !geoValuesEqual(
        measurement.middlePoint.y,
        updatedMeasurement.middlePoint.y
      ) ||
      !geoValuesEqual(
        measurement.middlePoint.z,
        updatedMeasurement.middlePoint.z
      );

    this.syncMeasurementWithMarkup(
      measurement,
      markup,
      startChanged || endChanged || middleChanged,
      (m, sourceViewer) => ({
        ...m,
        startPoint: updatedMeasurement.startPoint,
        endPoint: updatedMeasurement.endPoint,
        middlePoint: updatedMeasurement.middlePoint,
        value: updatedMeasurement.value,
        markups: this.getViewerMarkups(sourceViewer, m),
      }),
      'Angle measurement markup not found in viewers'
    );
  }

  private hasPicksChanged(
    measurementPicks: SnapPick[],
    markupPicks: SnapPick[]
  ): boolean {
    if (measurementPicks.length !== markupPicks.length) {
      return true;
    }
    return measurementPicks.some((p, index) => {
      const markupPick = markupPicks[index];
      const pPoint = p.point;
      const mPoint = markupPick.point;

      // If one has a point and the other doesn't, they're different
      if (!pPoint || !mPoint) {
        return pPoint !== mPoint;
      }

      return (
        pPoint.x !== mPoint.x || pPoint.y !== mPoint.y || pPoint.z !== mPoint.z
      );
    });
  }

  public setItemSavedStatus(
    id: string,
    savedStatus: MeasurementSaveStatus
  ): void {
    this.setItems((prev) => {
      return prev.map((item) => {
        if (item.id === id) return { ...item, savedStatus };
        return item;
      });
    });
  }

  private readonly handleMarkupModified =
    (
      viewerWrapper: Web3DViewerWrapper,
      previousViewerWrappers: Web3DViewerWrapper[] | undefined
    ) =>
    (e: Event) => {
      //wait for other synchronous function will be completed
      queueMicrotask(() => {
        const markup = (e as CustomEvent<Markup>).detail;
        if (!markup) {
          return;
        }
        const measurementUpdated = this.updateMeasurementForMarkup(markup);
        if (measurementUpdated) {
          return;
        }
        if (markup.origin === REPLICATION_ORIGIN) {
          return;
        }
        // Guard against stale replica events (NUMARK-797):
        //
        // When a user drags rapidly, multiple `markupmodified` events fire for
        // the source markup (e.g. B_pano) and queue microtasks A1, A2, ...
        // A1 creates replica markups on other viewers (D_3d, E_planar), which
        // each fire their own `markupmodified`, queuing microtasks B1, C1.
        // Because microtasks run FIFO, A2 executes before B1/C1. A2 calls
        // `removeById` on D_3d/E_planar and replaces them with fresh replicas.
        // When B1/C1 finally run, D_3d/E_planar are no longer in the plugin or
        // in `measurement.markups` — `updateMeasurementForMarkup` returns false
        // (not found), and without this guard the handler would treat the stale
        // replica as a brand-new user-drawn measurement, creating a phantom
        // duplicate visible in the panorama plane.
        //
        // The deeper cause is that `syncMeasurementWithMarkup` calls
        // `updateMeasurement` (setItems) with only the source markup, then pushes
        // replicas onto the array afterward — see the TODO on that method for the
        // proper long-term fix.
        const markupPlugin = viewerWrapper.markupPlugin;
        const isMarkupStillAlive = markupPlugin
          .getMarkups()
          .some((m) => m.id === markup.id);
        if (!isMarkupStillAlive) {
          return;
        }

        const label = getLabelFromMarkup(markup, this.items());
        const measurement = convertMarkupToMeasurement(
          markup,
          label,
          this.pointMeasurementSettings()
        );
        if (!measurement) {
          return;
        }

        // Apply last used style if available for this measurement type
        this.styleManager.applyLastUsedStyle(measurement, markup);

        // Apply chainage formatting to the 3D distance label for alignment measurements
        const alignSettings = this.alignmentSettings();
        if (measurement.type === 'alignment' && alignSettings) {
          applyChainageFormatterToMarkup(
            markup,
            alignSettings,
            this.formattingSettings()
          );
        }

        this.setItems((prevMeasurements) => {
          return [measurement, ...prevMeasurements];
        });

        stampCanonicalId(markup, measurement.id);

        // Create markups in other panes before setting selection, so the
        // selection effect can find markups in all viewers.
        if (previousViewerWrappers != undefined) {
          // Use measurement color if available (which includes last used style), otherwise use markup color
          const colorToUse = measurement.color
            ? rgbToWeb3dColor(measurement.color)
            : markup.color;
          this.createMeasurement(
            measurement,
            this.viewerWrappers().filter(
              (v) => v.viewer != viewerWrapper.viewer
            ),
            colorToUse,
            REPLICATION_ORIGIN
          );
        }

        this.setSelectedItemId(measurement.id);
      });
    };

  private readonly handleMarkupRemoved = (e: Event): void => {
    // wait for other synchronous function will be completed
    queueMicrotask(() => {
      const markup = (e as CustomEvent<Markup>).detail;
      const measurement = this.items().find((m) =>
        this.getValidMarkups(m.markups).some((m) => m.id == markup.id)
      );
      if (measurement) {
        if (measurement.type === 'volume') {
          // Boundary polygon is a display/selection proxy only; do not delete the volume.
          const volumeMeasurement = measurement as VolumeMeasurementType;
          this.updateMeasurement({
            ...volumeMeasurement,
            markups: this.getValidMarkups(volumeMeasurement.markups).filter(
              (m) => m.id !== markup.id
            ),
          });
          return;
        }
        this.removeMeasurement(measurement.id);
      }
    });
  };

  private readonly handleMarkupSelection =
    (viewerWrapper: Web3DViewerWrapper) =>
    (_e: Event): void => {
      queueMicrotask(() => {
        this.processMarkupSelection(viewerWrapper);
      });
    };

  /**
   * Viewer `selection` is highlight only. The toolbar target is `selectedItemId`,
   * mutated here only on a positive measurement pick. Empty, leftover, CAD, and
   * hide events must not deselect. Clear via explicit UI, delete, or picking
   * another measurement.
   */
  private processMarkupSelection(viewerWrapper: Web3DViewerWrapper): void {
    if (this.isMarkupSelectionSuppressed()) return;

    const selectedVolume = this.items()
      .filter((m) => m.type === 'volume')
      .find((m) => {
        const ids = viewerWrapper.viewer.selection.get(
          VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX + m.id
        );
        return ids != null && ids.length > 0;
      });

    if (selectedVolume) {
      this.setSelectedItemId(selectedVolume.id);
      return;
    }

    const selectedMeasurement =
      this.findMeasurementFromMarkupSelection(viewerWrapper);
    if (selectedMeasurement) {
      this.setSelectedItemId(selectedMeasurement.id);
    }
  }

  private findMeasurementFromMarkupSelection(
    viewerWrapper: Web3DViewerWrapper
  ): Measurement | undefined {
    const selectedMarkupIds =
      viewerWrapper.viewer.selection.get('markup') || [];
    return this.items().find((measurement) =>
      this.getValidMarkups(measurement.markups).some((markup) =>
        selectedMarkupIds.some((selectedId) => markup.id == selectedId)
      )
    );
  }

  private addEventListenersToViewer(
    viewerWrapper: Web3DViewerWrapper,
    previousViewers: Web3DViewerWrapper[] | undefined
  ): void {
    const markupModifiedHandler = this.handleMarkupModified(
      viewerWrapper,
      previousViewers
    );
    const markupRemovedHandler = this.handleMarkupRemoved;
    const markupSelectionHandler = this.handleMarkupSelection(viewerWrapper);

    viewerWrapper.viewer.addEventListener(
      'markupmodified' as MarkupEventName.Modified,
      markupModifiedHandler
    );
    viewerWrapper.viewer.addEventListener(
      'markupremoved' as MarkupEventName.Removed,
      markupRemovedHandler
    );
    viewerWrapper.viewer.addEventListener('selection', markupSelectionHandler);
    this.viewerEventListeners.set(viewerWrapper.viewer, [
      { event: 'markupmodified', handler: markupModifiedHandler },
      { event: 'markupremoved', handler: markupRemovedHandler },
      { event: 'selection', handler: markupSelectionHandler },
    ]);
  }

  private removeEventListenersForViewer(
    viewerWrapper: Web3DViewerWrapper
  ): void {
    const listeners = this.viewerEventListeners.get(viewerWrapper.viewer);
    if (listeners) {
      listeners.forEach(({ event, handler }) => {
        viewerWrapper.viewer.removeEventListener(event, handler);
      });
      this.viewerEventListeners.delete(viewerWrapper.viewer);
    }
  }

  private removeAllEventListeners(): void {
    this.viewerEventListeners.forEach((listeners, viewer) => {
      listeners.forEach(({ event, handler }) => {
        viewer.removeEventListener(event, handler);
      });
    });
    this.viewerEventListeners.clear();
  }

  public removeMeasurement(measurementId: string): void {
    if (this.selectedItemId() === measurementId) {
      this.setSelectedItemId(null);
    }
    this.setItems((prev) => prev.filter((m) => m.id !== measurementId));
  }

  public removeAllMeasurements(): void {
    this.setSelectedItemId(null);
    this.setItems([]);
  }

  public toggleMeasurementVisibility(
    measurement: Measurement,
    options?: ToggleMeasurementVisibilityOptions
  ): void {
    measurement.hidden = !measurement.hidden;
    const keepSelection = options?.keepSelection === true;
    this.getValidMarkups(measurement.markups).forEach((markup) => {
      this.setMarkupVisibility(markup, !measurement.hidden);
    });
    this.viewerWrappers().forEach((wrapper) => {
      wrapper.viewer.dispatchEvent(new Event('markupvisibilitychanged'));
      wrapper.viewer.waitForRender();
    });
    if (
      measurement.hidden &&
      !keepSelection &&
      this.selectedItemId() === measurement.id
    ) {
      this.setSelectedItemId(null);
    }
    this.updateMeasurement(measurement);
  }

  /**
   * Handles visibility for all measurements agnostic of type
   *
   * @param visible
   */
  public setAllMeasurementsVisibility(visible: boolean): void {
    const currentItems = this.items();
    const newItems = currentItems.map((item) => {
      const updatedItem = {
        ...item,
        hidden: !visible,
      };
      this.getValidMarkups(updatedItem.markups).forEach((markup) => {
        this.setMarkupVisibility(markup, visible && !updatedItem.hidden);
      });
      this.styleManager.updateLastUsedStyle(updatedItem, item);
      return updatedItem;
    });
    this.setItems(newItems);
  }

  private clearMarkupSelection(): void {
    for (const vw of this.viewerWrappers()) {
      vw.viewer.selection.clear('markup');
    }
  }

  private clearVolumeTilesetSelection(measurementId?: string): void {
    const volumeMeasurements = measurementId
      ? this.items().filter(
          (m) => m.type === 'volume' && m.id === measurementId
        )
      : this.items().filter((m) => m.type === 'volume');

    for (const vw of this.viewerWrappers()) {
      for (const volume of volumeMeasurements) {
        vw.viewer.selection.clear(
          VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX + volume.id
        );
      }
    }
  }

  private setMarkupRenderOrder(markup: Markup, order: number): void {
    markup.traverse((child) => {
      child.renderOrder = order;
    });
  }

  private setMarkupVisibility(markup: Markup, visible: boolean): void {
    markup.visible = visible;
  }

  private bringMarkupsToFront(markups: Markup[]): void {
    for (const markup of markups) {
      this.setMarkupRenderOrder(markup, SELECTED_MARKUP_RENDER_ORDER);
    }
  }

  private resetAllMarkupsRenderOrder(): void {
    for (const measurement of this.items()) {
      for (const markup of this.getValidMarkups(measurement.markups)) {
        this.setMarkupRenderOrder(markup, DEFAULT_MARKUP_RENDER_ORDER);
      }
    }
  }

  public updateMarkupColor(color: string): void {
    for (const vw of this.viewerWrappers()) {
      vw.viewer.plugins.get(MarkupPlugin)!.settings.color = new Color(color);
    }
  }

  public getLastUsedColorForType(type: MeasurementType): RGBFormat | undefined {
    return this.styleManager.getLastUsedColor(type);
  }

  public setLastUsedColorForType(
    type: MeasurementType,
    color: RGBFormat
  ): void {
    this.styleManager.setLastUsedColor(type, color);
  }

  public getLastUsedFillOpacityForType(
    type: MeasurementType
  ): number | undefined {
    return this.styleManager.getLastUsedFillOpacity(type);
  }

  public setLastUsedFillOpacityForType(
    type: MeasurementType,
    fillOpacity: number
  ): void {
    this.styleManager.setLastUsedFillOpacity(type, fillOpacity);
  }

  public setVolumeTilesetOpacity(measurementId: string, opacity: number): void {
    const modelId = VOLUME_MEASUREMENT_TILESET_MODEL_ID_PREFIX + measurementId;
    for (const vw of this.viewerWrappers()) {
      const model = vw.viewer.getModel(modelId);
      setTilesetModelOpacity(model, opacity);
    }
  }

  public getLastUsedLineThicknessForType(
    type: MeasurementType
  ): number | undefined {
    return this.styleManager.getLastUsedLineThickness(type);
  }

  public getLastUsedLineStyleForType(
    type: MeasurementType
  ): LineStyle | undefined {
    return this.styleManager.getLastUsedLineStyle(type);
  }

  public setLastUsedLineThicknessForType(
    type: MeasurementType,
    lineThickness: number
  ): void {
    this.styleManager.setLastUsedLineThickness(type, lineThickness);
  }

  public setLastUsedLineStyleForType(
    type: MeasurementType,
    lineStyle: LineStyle
  ): void {
    this.styleManager.setLastUsedLineStyle(type, lineStyle);
  }

  public enableMarkupDelete(enableDelete: boolean): void {
    for (const vw of this.viewerWrappers()) {
      vw.viewer.plugins.get(MarkupPlugin)!.settings.deleteEnabled =
        enableDelete;
    }
  }

  public loadMeasurements(measurements: Measurement[]): void {
    this.removeAllMeasurements();

    const loaded = measurements.map((measurement) => ({
      ...measurement,
      markups: [],
    }));
    batch(() => {
      this.setItems([...loaded]);

      for (const viewerWrapper of this.viewerWrappers()) {
        projectMeasurementsOntoViewer(
          this.getSeedContext(),
          viewerWrapper,
          REPLICATION_ORIGIN
        );
      }
    });
  }

  public addMeasurement(measurement: Measurement): boolean {
    // For volume measurements, create the boundary polygon markup before adding
    // to the store (mirroring the loadMeasurements pattern) so the boundary is
    // rendered immediately after save without requiring a page reload.
    if (measurement.type === 'volume') {
      this.createMeasurement(measurement);
    }
    return this.addItem(measurement);
  }

  /**
   * Restores measurements (e.g. alignment) from reserved view data alongside
   * native Connect markups, appending to the store without clearing existing
   * ones (NUMARK-968). On a mid-way `createMeasurement` failure, partial markups
   * are removed before rethrowing so cleanup (driven by `items()`) isn't bypassed.
   */
  public restoreMeasurements(measurements: Measurement[]): void {
    if (measurements.length === 0) return;

    const ctx = this.getSeedContext();
    const viewers = this.viewerWrappers();
    for (const viewerWrapper of viewers) {
      ctx.applyFormatting(viewerWrapper);
    }

    for (const measurement of measurements) {
      try {
        measurement.markups = [];
        batch(() => {
          this.setItems((prev) => [...prev, measurement]);
          for (const viewerWrapper of viewers) {
            projectMeasurementsOntoViewer(
              ctx,
              viewerWrapper,
              REPLICATION_ORIGIN,
              { applyFormatting: false }
            );
          }
        });
      } catch (error) {
        this.setItems((prev) =>
          prev.filter((item) => item.id !== measurement.id)
        );
        this.removeMeasurementMarkupsFromViewers(measurement);
        throw error;
      }
    }
  }

  /** Removes a measurement's markups from every viewer to undo a partial restore. */
  private removeMeasurementMarkupsFromViewers(measurement: Measurement): void {
    const ids = this.getValidMarkups(measurement.markups).map((m) => m.id);
    if (ids.length === 0) return;

    for (const viewerWrapper of this.viewerWrappers()) {
      const markupPlugin = viewerWrapper.viewer.plugins.get(MarkupPlugin);
      if (!markupPlugin) continue;

      const markupIds = markupPlugin
        .getMarkups()
        .filter((m) => ids.some((id) => id === m.id))
        .map((m) => m.id);

      if (markupIds.length) {
        markupPlugin.removeById(markupIds);
      }
    }
  }

  // TODO: Add support for slope measurement type once its available in Numark
  public loadMeasurementsForConnectView(markups: ConnectMarkup[]): void {
    this.removeAllMeasurements();

    // Transform API data to measurements
    const measurements = markups
      .map((markup) => processMarkup(markup))
      .filter((markup): markup is MeasurementMarkup => markup !== null)
      .map((markup) => transformTcMarkupToMeasurement(markup))
      .filter((m): m is Measurement => m !== null)
      .map((measurement) => ({ ...measurement, markups: [] }));

    batch(() => {
      this.setItems([...measurements]);

      for (const viewerWrapper of this.viewerWrappers()) {
        projectMeasurementsOntoViewer(
          this.getSeedContext(),
          viewerWrapper,
          REPLICATION_ORIGIN
        );
      }
    });
  }

  private requirePolygonMeasurementTool(
    vw: Web3DViewerWrapper
  ): PolygonMeasurementTool {
    const polygonMeasurementTool = vw.viewer.tools.get(PolygonMeasurementTool);
    if (!polygonMeasurementTool) {
      throw new Error('PolygonMeasurementTool is not available on the viewer');
    }
    return polygonMeasurementTool;
  }

  private pushStampedMeasurementMarkup(
    measurement: Measurement,
    viewerMarkup: Markup
  ): void {
    stampCanonicalId(viewerMarkup, measurement.id);
    measurement.markups.push(viewerMarkup);
  }

  private createMeasurement(
    measurement: Measurement,
    targetViewerWrappers: Web3DViewerWrapper[] = this.viewerWrappers(),
    color?: Color,
    origin?: unknown
  ): void {
    // Use color from measurement if available, otherwise use provided color or default
    const measurementColor = measurement.color
      ? new Color(
          measurement.color.r / RGB_FACTOR,
          measurement.color.g / RGB_FACTOR,
          measurement.color.b / RGB_FACTOR
        )
      : color || DEFAULT_MAGENTA_COLOR;
    for (const vw of targetViewerWrappers) {
      switch (measurement.type) {
        case 'line': {
          const measurementTool = vw.viewer.tools.get(MeasurementTool);
          if (!measurementTool) {
            throw new Error('MeasurementTool is not available on the viewer');
          }
          const lineMarkup = measurementTool.add(
            {
              snapType: SnapType.POINT as number,
              point: measurement.startPoint,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.endPoint,
            },
            measurementColor,
            origin
          );

          this.pushStampedMeasurementMarkup(measurement, lineMarkup);
          break;
        }
        case 'point': {
          const pointMarkupTool = vw.viewer.tools.get(PointMarkupTool);
          if (!pointMarkupTool) {
            throw new Error('PointMarkupTool is not available on the viewer');
          }
          const pointMarkup = pointMarkupTool.add(
            {
              point: partialPointToVector3(measurement),
            },
            measurementColor,
            origin
          );
          pointMarkup.imageUrl = measurement.pointSymbol?.url || '';
          pointMarkup.imagePosition =
            measurement.pointSymbol?.position || ImagePosition.Center;
          applyPointLabel(
            pointMarkup,
            vw.viewer,
            measurement.label,
            measurement.showXYCoordinates ?? true,
            measurement.showZCoordinate ?? true
          );
          this.pushStampedMeasurementMarkup(measurement, pointMarkup);
          break;
        }
        case 'angle': {
          const aTool = vw.viewer.tools.get(AngleMeasurementTool);
          if (!aTool) {
            throw new Error(
              'AngleMeasurementTool is not available on the viewer'
            );
          }
          const prevSnapTypes = [...vw.viewer.settings.snapTypes];
          vw.viewer.settings.snapTypes = [SnapType.POINT as number];
          const angleMarkup = aTool.add(
            {
              snapType: SnapType.POINT as number,
              point: measurement.startPoint,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.endPoint,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.middlePoint,
            },
            measurementColor,
            origin
          );

          if (!angleMarkup) {
            getLogger().warn(
              'Attempted to push a null or undefined angle markup',
              {
                measurementId: measurement.id,
              }
            );
          } else {
            this.pushStampedMeasurementMarkup(measurement, angleMarkup);
          }
          vw.viewer.settings.snapTypes = prevSnapTypes;
          break;
        }
        case 'alignment': {
          const alignmentTool = vw.viewer.tools.get(AlignmentMeasurementTool);
          if (!alignmentTool) {
            throw new Error(
              'AlignmentMeasurementTool is not available on the viewer'
            );
          }
          const prevAlignSnapTypes = [...vw.viewer.settings.snapTypes];
          vw.viewer.settings.snapTypes = [SnapType.POINT as number];
          const alignmentMarkup = alignmentTool.add(
            {
              snapType: SnapType.POINT as number,
              point: measurement.ground,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.point,
            },
            {
              snapType: SnapType.POINT as number,
              point: measurement.alignment,
            },
            measurement.value,
            measurementColor,
            origin
          );
          const alignSettingsForMarkup = this.alignmentSettings();
          if (alignSettingsForMarkup) {
            applyChainageFormatterToMarkup(
              alignmentMarkup,
              alignSettingsForMarkup,
              this.formattingSettings()
            );
          }

          this.pushStampedMeasurementMarkup(measurement, alignmentMarkup);
          vw.viewer.settings.snapTypes = prevAlignSnapTypes;
          break;
        }
        case 'polyline': {
          const polylineMeasurementTool = vw.viewer.tools.get(
            PolylineMeasurementTool
          );
          if (!polylineMeasurementTool) {
            throw new Error(
              'PolylineMeasurementTool is not available on the viewer'
            );
          }
          const polylineMarkup = polylineMeasurementTool.add(
            measurement.picks,
            measurementColor,
            origin
          );
          polylineMarkup.showSegmentDistances =
            measurement.showSegmentDistances;
          polylineMarkup.showSegmentSlopes = measurement.showSegmentSlopes;
          polylineMarkup.showHorizontalMeasurements =
            measurement.showHorizontalMeasurements;
          polylineMarkup.showVerticalMeasurements =
            measurement.showVerticalMeasurements;
          polylineMarkup.slopeFormatter = (vertical, horizontal) =>
            slopeFormatter(
              vertical,
              horizontal,
              measurement.slopeFormat || this.slopeFormat()
            );
          this.pushStampedMeasurementMarkup(measurement, polylineMarkup);
          break;
        }
        case 'area': {
          const polygonMeasurementTool = this.requirePolygonMeasurementTool(vw);
          // Clone picks before passing to the tool. web3d stores references to
          // the Vector3 objects it receives and mutates them in-place when the
          // user drags a handle (same issue documented in createLineMeasurement).
          // By handing the tool independent clones, measurement.picks retains
          // its own Vector3 instances so that hasPicksChanged() can detect
          // position changes correctly.
          const picksForMarkup = clonePicks(measurement.picks);
          const polygonMarkup = polygonMeasurementTool.add(
            picksForMarkup,
            measurementColor,
            origin
          );
          polygonMarkup.fillOpacity = measurement.fillOpacity;
          polygonMarkup.lineStyle = measurement.lineStyle;
          polygonMarkup.lineThickness = measurement.lineThickness;
          this.pushStampedMeasurementMarkup(measurement, polygonMarkup);
          break;
        }
        case 'volume':
        case 'passCount': {
          const picks = boundaryInclusiveToSnapPicks(
            measurement.parameters.boundaryInclusive
          );
          if (!picks) {
            break;
          }
          const polygonMeasurementTool = this.requirePolygonMeasurementTool(vw);
          // polygonMeasurementTool.add() expects a closed polygon (last pick == first
          // pick). boundaryInclusiveToSnapPicks strips the GeoJSON closing duplicate,
          // so we re-add it here; without it the tool silently drops the last vertex.
          // Clone the closing pick's point so the tool cannot corrupt the first
          // vertex via in-place Vector3 mutation (same defence as clonePicks).
          const closedPicks = [
            ...picks,
            { ...picks[0], point: picks[0].point?.clone() },
          ];
          const polygonMarkup = polygonMeasurementTool.add(
            closedPicks,
            measurementColor,
            origin
          );
          // Boundary is display/selection only; do not allow vertex drag like area measurements.
          polygonMarkup.editable = false;
          polygonMarkup.fillOpacity =
            DEFAULT_POLYGON_MEASUREMENT_SETTINGS.fillOpacity;
          polygonMarkup.lineStyle =
            measurement.lineStyle ??
            DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineStyle;
          polygonMarkup.lineThickness =
            measurement.lineThickness ??
            DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineThickness;
          this.pushStampedMeasurementMarkup(measurement, polygonMarkup);
          break;
        }
        default:
          throw new UnreachableCaseError(measurement);
      }
    }
  }

  public getBoundaryPointsFromPolygonMarkup = (
    pickId: number | string
  ): Vector3[] | undefined =>
    getBoundaryPointsFromPolygonMarkup(this.items(), pickId);

  public updateMeasurement(updatedMeasurement: Measurement): void {
    const items = this.items();
    let currentMeasurement: Measurement | undefined;
    let mergedMeasurement: Measurement | undefined;
    const newItems = items.map((item) => {
      if (item.id === updatedMeasurement.id) {
        currentMeasurement = item;
        const merged = { ...item, ...updatedMeasurement };
        mergedMeasurement = merged;
        return merged;
      }
      return item;
    });

    // For volume measurements, determine whether the boundary geometry changed
    // (which requires a full markup remove/recreate) or only display properties
    // changed (name, color, visibility) which can be applied in-place without
    // triggering selection events that would close the panel.
    if (updatedMeasurement.type === 'volume' && mergedMeasurement) {
      const volumeCurrent = currentMeasurement as VolumeMeasurementType;
      const volumeUpdated =
        updatedMeasurement as Partial<VolumeMeasurementType>;

      const applyToExistingMarkups = (
        fn: (existingMarkups: Markup[], vw: Web3DViewerWrapper) => void
      ): void => {
        for (const vw of this.viewerWrappers()) {
          const existingMarkups = this.getViewerMarkups(
            vw.viewer,
            currentMeasurement!
          );
          fn(existingMarkups, vw);
        }
      };

      const updateExistingMarkupsProperty = (
        property: 'color',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any
      ): void => {
        applyToExistingMarkups((existingMarkups) => {
          for (const markup of existingMarkups) {
            (markup as Markup)[property] = value;
          }
        });
      };

      const updateExistingMarkupsVisibility = (visible: boolean): void => {
        applyToExistingMarkups((existingMarkups) => {
          for (const markup of existingMarkups) {
            this.setMarkupVisibility(markup, visible);
          }
        });
      };

      const boundaryChanged =
        volumeUpdated.parameters?.boundaryInclusive !== undefined &&
        volumeUpdated.parameters.boundaryInclusive !==
          volumeCurrent.parameters?.boundaryInclusive;

      if (boundaryChanged) {
        // // Boundary geometry changed — must remove and recreate markups.
        applyToExistingMarkups((existingMarkups, vw) => {
          if (existingMarkups.length > 0) {
            vw.markupPlugin.removeById(existingMarkups.map((m) => m.id));
          }
        });
        mergedMeasurement.markups = [];

        this.createMeasurement(mergedMeasurement);
        // createMeasurement() always adds markups as visible, so respect the
        // measurement's stored visibility state.
        const isHidden =
          updatedMeasurement.hidden ?? currentMeasurement?.hidden ?? false;
        if (isHidden) {
          this.getValidMarkups(mergedMeasurement.markups).forEach((markup) => {
            this.setMarkupVisibility(markup, false);
          });
        }
      } else {
        const hasStylePropertyUpdate =
          updatedMeasurement.color !== undefined ||
          updatedMeasurement.hidden !== undefined ||
          updatedMeasurement.fillOpacity !== undefined ||
          volumeUpdated.lineThickness !== undefined ||
          volumeUpdated.lineStyle !== undefined;

        if (hasStylePropertyUpdate) {
          // The merge may have overwritten markups with a stale array from
          // updatedMeasurement (e.g. { ...m, color } captured before a prior
          // boundary edit replaced markups). Restore the live store markups.
          mergedMeasurement.markups = currentMeasurement!.markups;
        }

        // Property-only change (color, visibility, boundary stroke) — update
        // existing markups in-place to avoid triggering selection events.
        if (updatedMeasurement.color) {
          const newColor = rgbToWeb3dColor(updatedMeasurement.color);
          updateExistingMarkupsProperty('color', newColor);
        }
        if (updatedMeasurement.fillOpacity !== undefined) {
          this.setVolumeTilesetOpacity(
            updatedMeasurement.id,
            updatedMeasurement.fillOpacity
          );
        }
        if (updatedMeasurement.hidden !== undefined) {
          updateExistingMarkupsVisibility(!updatedMeasurement.hidden);
        }
        if (volumeUpdated.lineThickness !== undefined) {
          applyToExistingMarkups((existingMarkups) => {
            for (const markup of existingMarkups) {
              (markup as PolygonMeasurement).lineThickness =
                volumeUpdated.lineThickness!;
            }
          });
        }
        if (volumeUpdated.lineStyle !== undefined) {
          applyToExistingMarkups((existingMarkups) => {
            for (const markup of existingMarkups) {
              (markup as PolygonMeasurement).lineStyle =
                volumeUpdated.lineStyle!;
            }
          });
        }
      }
    }

    this.setItems(newItems);

    // Save last used style for this measurement type when color or symbol changes
    if (currentMeasurement) {
      this.styleManager.updateLastUsedStyle(
        updatedMeasurement,
        currentMeasurement
      );
    }
  }

  public getViewerMarkups(
    viewer: Web3DViewer,
    measurement: Measurement
  ): Markup[] {
    const markupPlugin = viewer.plugins.get(MarkupPlugin);
    if (!markupPlugin) {
      throw new Error('MarkupPlugin is not available on the viewer');
    }
    return markupPlugin
      .getMarkups()
      .filter((m) =>
        this.getValidMarkups(measurement.markups).some((mp) => mp.id === m.id)
      );
  }
}

export {
  createMeasurementApiRefreshRunner,
  mergeRefreshedMeasurement,
  shouldRefreshMeasurementFromApi,
} from './refresh-measurement-from-api';
export type {
  MeasurementApiRefreshContext,
  MeasurementFetchResult,
  MeasurementRefreshTarget,
  RefreshMeasurementFromApiOptions,
} from './refresh-measurement-from-api';
